/**
 * EditableImportTitle — the Import Detail `<h1>`, with a pencil that turns it
 * into an input.
 *
 * The title is the ONE thing on this page a recruiter can fix themselves. It
 * arrives from whatever produced the file — a LinkedIn Recruiter export named
 * `linkedin_profiles_2026-08-04_AI_review_color_head.json`, a Baby Chops
 * scrape named after a search id and a timestamp — and none of those are what
 * the list is called in anyone's head.
 *
 * Two decisions worth knowing before editing this:
 *
 * 1. **The extension is neither editable nor SHOWN.** It belongs to the stored
 *    OBJECT rather than to the title: the API re-applies it from `s3_key` on
 *    every write (`domain.normalize_rename`).
 *
 *    It used to render beside the input as fixed text so the recruiter could
 *    see what the server would re-apply. Since 2026-08-26 it is hidden
 *    everywhere — heading, editor, firm-wide table, picker, history panel —
 *    and the editor was the last surface where it would otherwise have leaked.
 *    `rename-import-extension` no longer exists; `rename.spec.ts` and
 *    `EditableImportTitle.test.tsx` assert its ABSENCE.
 *
 *    Note the correction is a server behaviour, not a UI guarantee. A recruiter
 *    who types `report.csv` into the stem of a JSON-backed list still gets
 *    `report.json` back, silently — the server corrects rather than rejects,
 *    because a title that lies about its file is worse than a surprising one.
 *    Hiding the extension makes that case LESS visible than it was, which is
 *    the cost of the change: previously the fixed text next to the input hinted
 *    at what would happen.
 * 2. **Who may rename is the server's call, not this component's.** It renders
 *    the pencil when `detail.can_rename` says so and never recomputes the rule
 *    from emails on the client. `PATCH` re-checks and answers 403 regardless,
 *    so this only decides whether to OFFER the action.
 * 3. **The response is the source of truth, not the local input.** Save closes
 *    the editor and invalidates the queries; the rendered title comes back
 *    from the server. That is what keeps the trimmed / extension-corrected
 *    value on screen instead of the raw keystrokes.
 *
 * The mutation invalidates the LIST as well as the detail: the same title is
 * on the Contact Lists table and inside the persisted import-selection bar,
 * and a rename that only updates the page you are on is how two screens start
 * disagreeing about one row.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Pencil } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button } from "@nu-assist/ui";

import type { ImportsClient } from "../../../clients/imports.client";
import { displayTitle, splitTitle, titleError } from "./importTitle";

export interface EditableImportTitleProps {
  importId: string;
  /** `detail.filename`, or undefined while the detail query is in flight. */
  filename: string | undefined;
  /**
   * `detail.can_rename` — the caller is this list's owner or creator.
   *
   * Undefined while the detail query is in flight, which reads as "not yet",
   * same as `false`. Defaulting the OTHER way would flash a pencil at everyone
   * on every page load and then retract it.
   */
  canRename: boolean | undefined;
  client: Pick<ImportsClient, "renameImport">;
}

export function EditableImportTitle({
  importId,
  filename,
  canRename,
  client,
}: EditableImportTitleProps) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [stem, setStem] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Only the stem is read now: the extension is no longer rendered anywhere,
  // and the SERVER re-applies the stored one to whatever stem is submitted
  // (`domain.normalize_rename`), so the client never needs to send or show it.
  const { stem: storedStem } = splitTitle(filename ?? "");

  const mutation = useMutation({
    mutationFn: (next: string) => client.renameImport(importId, next),
    onSuccess: () => {
      setEditing(false);
      queryClient.invalidateQueries({ queryKey: ["import-detail", importId] });
      // Prefix match — every page/filter combination of the Contact Lists
      // table shares the `["imports", "list", ...]` head.
      queryClient.invalidateQueries({ queryKey: ["imports", "list"] });
    },
  });

  // Focus and select on open, so the common case (replace the whole machine-
  // generated name) is one keystroke rather than a manual select-all.
  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  function open() {
    setStem(storedStem);
    mutation.reset();
    setEditing(true);
  }

  function cancel() {
    mutation.reset();
    setEditing(false);
  }

  // The local rule first (no round-trip for a title we already know is
  // invalid), then whatever the server says — it is the authority, and its
  // 400 detail is written for the recruiter.
  const localError = titleError(stem);
  const serverError = mutation.isError
    ? ((mutation.error as Error).message ?? "Could not rename this people list.")
    : null;
  const error = localError ?? serverError;
  const unchanged = stem.trim() === storedStem.trim();

  function save() {
    if (localError || unchanged || mutation.isPending) return;
    mutation.mutate(stem.trim());
  }

  if (!editing) {
    return (
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {/* The STEM only. The extension belongs to the stored object, not to
            the name the recruiter gave this list, and it is never shown —
            here, in the editor, in the firm-wide table, or in the history
            panel. Everything that STORES or SENDS the name still carries it:
            `original_filename`, the rename round-trip, the download's
            `Content-Disposition`. */}
        <h1 data-testid="import-title" className="truncate text-h1 text-text">
          {filename ? displayTitle(filename) : "People list"}
        </h1>
        {/* Hidden until the title exists (renaming a list that has not loaded
            would post a stem derived from the empty string) AND until the
            server says this caller owns it. */}
        {filename && canRename ? (
          <button
            type="button"
            data-testid="rename-import"
            aria-label="Rename people list"
            title="Rename people list"
            onClick={open}
            className="shrink-0 rounded-xs p-1 text-text-muted hover:bg-surface hover:text-text focus:outline-none focus-visible:ring-1 focus-visible:ring-border-hover"
          >
            <Pencil aria-hidden className="h-4 w-4" />
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1">
      <div className="flex min-w-0 items-center gap-2">
        <input
          ref={inputRef}
          type="text"
          value={stem}
          data-testid="rename-import-input"
          aria-label="People list title"
          aria-invalid={error ? true : undefined}
          disabled={mutation.isPending}
          onChange={(event) => setStem(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              save();
            }
            if (event.key === "Escape") {
              event.preventDefault();
              cancel();
            }
          }}
          className="min-w-0 flex-1 rounded-sm border border-border bg-bg px-2.5 py-1.5 text-body text-text placeholder:text-text-muted hover:border-border-hover focus:border-border-hover focus:outline-none disabled:text-text-secondary"
        />
        {/* The extension used to render here as fixed, uneditable text so the
            recruiter could see what the server would re-apply. It is hidden
            now (2026-08-26): the rule became "the extension is never part of
            the displayed name", and showing it in the editor while hiding it
            everywhere else would be the one place it leaked. `extension` is
            still read from the title and still round-trips — it is simply not
            drawn. */}
        <Button
          data-testid="rename-import-save"
          disabled={Boolean(localError) || unchanged || mutation.isPending}
          onClick={save}
        >
          {mutation.isPending ? "Saving…" : "Save"}
        </Button>
        <Button variant="secondary" data-testid="rename-import-cancel" onClick={cancel}>
          Cancel
        </Button>
      </div>
      {error ? (
        <p data-testid="rename-import-error" className="text-caption text-error">
          {error}
        </p>
      ) : null}
    </div>
  );
}
